import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { NAV_ITEMS, LINKS, shortCa, copyCa } from '@/data/content';

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

export default function Navbar({ loaded }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [caCopied, setCaCopied] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const isActive = (item) =>
    item.path === '/' ? pathname === '/' : pathname.startsWith(item.path);

  const go = (path) => {
    setOpen(false);
    setTimeout(() => navigate(path), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        className="fixed inset-x-0 top-0 z-[150]"
        initial={{ y: -80, opacity: 0 }}
        animate={loaded ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
        data-testid="navbar"
      >
        <div className="mx-auto w-[calc(100%-2rem)] max-w-[1400px] sm:w-[calc(100%-3rem)]">
          <motion.div
            className="flex items-center justify-between gap-4 rounded-2xl border transition-all duration-300"
            animate={{
              padding: scrolled ? '10px 18px' : '18px 24px',
              backgroundColor: scrolled ? 'rgba(5,11,24,0.82)' : 'rgba(5,11,24,0.35)',
              borderColor: scrolled ? 'rgba(0,200,255,0.3)' : 'rgba(0,200,255,0.12)',
              boxShadow: scrolled
                ? '0 8px 40px rgba(0,200,255,0.18), inset 0 0 24px rgba(0,200,255,0.05)'
                : '0 4px 24px rgba(5,11,24,0.4)'
            }}
            style={{ backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)' }}
          >
            <button
              data-testid="nav-logo-button"
              onClick={() => go('/')}
              className="flex items-center gap-2"
              aria-label="ARC TOONS — back to home"
            >
              <motion.img
                src="/assets/arctoons-logo-opt.webp"
                alt="ARC TOONS logo"
                className="object-contain"
                animate={{ height: scrolled ? 36 : 46, width: scrolled ? 36 : 46 }}
                transition={{ duration: 0.3 }}
              />
              <span className="hidden font-display text-xs font-bold uppercase tracking-[0.22em] text-snow sm:block">
                ARC TOONS
              </span>
            </button>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}-link`}
                  onClick={() => go(item.path)}
                  className={`relative rounded-lg px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-200 ${
                    isActive(item) ? 'text-electric' : 'text-snow/60 hover:text-snow'
                  }`}
                >
                  {item.label}
                  {isActive(item) && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-x-3 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-electric to-transparent"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="nav-ca-chip"
                onClick={async () => setCaCopied(await copyCa())}
                title="Copy contract address"
                className="hidden items-center gap-2 rounded-full border border-dashed border-electric/40 px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] text-electric/80 transition-colors duration-200 hover:text-electric xl:flex"
              >
                CA: {caCopied ? 'COPIED!' : shortCa()}
              </button>
              <a
                href={LINKS.buy}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-buy-button"
                className="light-sweep hidden rounded-full bg-gradient-to-r from-gold to-ember px-5 py-2 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-navy shadow-[0_0_20px_rgba(255,181,46,0.45)] transition-transform duration-200 hover:scale-105 sm:block"
              >
                BUY
              </a>
              <a
                href={LINKS.chart}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-chart-link"
                className="hidden items-center gap-2 rounded-full border border-electric/25 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-snow/70 transition-all duration-200 hover:border-electric hover:text-electric sm:flex"
              >
                CHART <span className="h-1.5 w-1.5 rounded-full bg-toongreen" />
              </a>
              <a
                href={LINKS.x}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ARC TOONS on X"
                data-testid="nav-x-button"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-electric/25 text-snow/80 transition-all duration-200 hover:border-electric hover:text-electric hover:shadow-[0_0_18px_rgba(0,200,255,0.5)]"
              >
                <XIcon className="h-3.5 w-3.5" />
              </a>
              <button
                data-testid="nav-hamburger"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-electric/25 text-snow lg:hidden"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
              >
                <span className="relative block h-3 w-4">
                  <span className="absolute left-0 top-0 h-0.5 w-full rounded bg-snow" />
                  <span className="absolute left-0 top-1.5 h-0.5 w-3/4 rounded bg-snow" />
                  <span className="absolute left-0 top-3 h-0.5 w-full rounded bg-snow" />
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[250] flex flex-col bg-navy/95"
            initial={{ opacity: 0, clipPath: 'circle(0% at 92% 6%)' }}
            animate={{ opacity: 1, clipPath: 'circle(150% at 92% 6%)' }}
            exit={{ opacity: 0, clipPath: 'circle(0% at 92% 6%)' }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            style={{ backdropFilter: 'blur(24px)' }}
          >
            <div className="scanlines flex items-center justify-between px-6 py-5">
              <img src="/assets/arctoons-logo-opt.webp" alt="ARC TOONS logo" className="h-11 w-11 object-contain" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-electric/30 text-snow"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {[...NAV_ITEMS, { id: 'community', label: 'COMMUNITY', path: '/community' }].map((item, i) => (
                <motion.button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}-link`}
                  onClick={() => go(item.path)}
                  initial={{ opacity: 0, x: -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.4, ease: 'easeOut' }}
                  className="group flex items-baseline gap-4 py-3 text-left"
                >
                  <span className="font-mono text-[11px] text-gold">0{i + 1}</span>
                  <span className="font-display text-3xl font-bold uppercase tracking-tight text-snow transition-colors group-hover:text-electric sm:text-4xl">
                    {item.label}
                  </span>
                </motion.button>
              ))}
            </nav>
            <motion.div
              className="flex flex-col gap-3 px-8 pb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <button
                onClick={async () => setCaCopied(await copyCa())}
                title="Copy contract address"
                className="text-center font-mono text-[10px] tracking-[0.3em] text-electric/70"
                data-testid="mobile-ca-chip"
              >
                CA: {caCopied ? 'COPIED!' : shortCa()}
              </button>
              <div className="flex gap-3">
                <a
                  href={LINKS.buy}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-buy-button"
                  className="flex-1 rounded-2xl bg-gradient-to-r from-gold to-ember py-4 text-center font-display text-sm font-bold uppercase tracking-[0.18em] text-navy"
                >
                  BUY
                </a>
                <a
                  href={LINKS.chart}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-chart-link"
                  className="flex-1 rounded-2xl border border-electric/25 py-4 text-center font-mono text-xs uppercase tracking-[0.18em] text-snow/70"
                >
                  CHART
                </a>
                <a
                  href={LINKS.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ARC TOONS on X"
                  data-testid="mobile-x-button"
                  className="flex w-14 items-center justify-center rounded-2xl border border-electric/25 text-snow"
                >
                  <XIcon className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
